import React, { useEffect, useMemo, useState } from 'react'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { Card } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { toast } from 'react-toastify'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  Legend
} from 'recharts'

function formatDate(date) {
  if (!date) return ''
  try {
    return new Date(date).toLocaleDateString('it-IT')
  } catch {
    return date
  }
}

function AiUsageAdmin() {
  const { user } = useAuth()
  const roleName = (user?.role_name || user?.role || '').toLowerCase()
  const isAdmin = roleName === 'admin'

  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [viewMode, setViewMode] = useState('detail')
  const [chartScale, setChartScale] = useState('day')
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    if (!isAdmin) return
    const now = new Date()
    const today = now.toISOString().slice(0, 10)
    const past = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000)
    const defaultFrom = past.toISOString().slice(0, 10)
    setFrom(defaultFrom)
    setTo(today)
  }, [isAdmin])

  const loadUsage = async () => {
    if (!from || !to) return
    try {
      setLoading(true)
      const res = await api.get('/ai/usage', {
        params: { from, to }
      })
      const data = res.data || {}
      setRows(Array.isArray(data.rows) ? data.rows : [])
    } catch (e) {
      const backendMessage =
        e?.response?.data?.message ||
        (e?.response?.status ? `Errore ${e.response.status} nel caricamento dei consumi AI` : null)
      toast.error(backendMessage || 'Errore nel caricamento dei consumi AI')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isAdmin) return
    if (!from || !to) return
    loadUsage()
  }, [isAdmin, from, to])

  const totalPeriodTokens = rows.reduce((acc, r) => acc + (r.totalTokens || 0), 0)

  const detailRows = rows

  const applyQuickRange = (scale) => {
    const now = new Date()
    const todayStr = now.toISOString().slice(0, 10)
    let fromDate

    if (scale === 'week') {
      const days = 11 * 7
      fromDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)
    } else if (scale === 'month') {
      const d = new Date(now)
      d.setMonth(d.getMonth() - 5)
      d.setDate(1)
      fromDate = d
    } else {
      const days = 6
      fromDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)
    }

    const fromStr = fromDate.toISOString().slice(0, 10)
    setChartScale(scale)
    setFrom(fromStr)
    setTo(todayStr)
  }

  const formatPromptValue = (promptTokens, completionTokens, totalTokens) => {
    const p = promptTokens || 0
    const c = completionTokens || 0
    const t = totalTokens || 0
    if (t > 0 && p + c === 0) {
      return 'n/d'
    }
    return p.toLocaleString('it-IT')
  }

  const formatCompletionValue = (promptTokens, completionTokens, totalTokens) => {
    const p = promptTokens || 0
    const c = completionTokens || 0
    const t = totalTokens || 0
    if (t > 0 && p + c === 0) {
      return 'n/d'
    }
    return c.toLocaleString('it-IT')
  }

  const modelRows = useMemo(() => {
    const map = new Map()
    for (const r of rows) {
      const key = r.model || 'N/D'
      if (!map.has(key)) {
        map.set(key, {
          model: key,
          promptTokens: 0,
          completionTokens: 0,
          totalTokens: 0
        })
      }
      const agg = map.get(key)
      agg.promptTokens += r.promptTokens || 0
      agg.completionTokens += r.completionTokens || 0
      agg.totalTokens += r.totalTokens || 0
    }
    return Array.from(map.values()).sort((a, b) => b.totalTokens - a.totalTokens)
  }, [rows])

  const userRows = useMemo(() => {
    const map = new Map()
    for (const r of rows) {
      const key = r.userId || 'null'
      if (!map.has(key)) {
        map.set(key, {
          userId: r.userId || null,
          userName: r.userName || null,
          email: r.email || null,
          promptTokens: 0,
          completionTokens: 0,
          totalTokens: 0
        })
      }
      const agg = map.get(key)
      agg.promptTokens += r.promptTokens || 0
      agg.completionTokens += r.completionTokens || 0
      agg.totalTokens += r.totalTokens || 0
    }
    return Array.from(map.values()).sort((a, b) => b.totalTokens - a.totalTokens)
  }, [rows])

  const chartDetailData = useMemo(() => {
    const map = new Map()
    for (const r of rows) {
      const d = new Date(r.day)
      let key
      let label
      let sortTime

      if (!Number.isNaN(d.getTime())) {
        if (chartScale === 'week') {
          const day = d.getDay()
          const diff = day === 0 ? -6 : 1 - day
          const monday = new Date(d)
          monday.setDate(d.getDate() + diff)
          sortTime = monday.getTime()
          key = `week_${sortTime}`
          label = `Sett. ${monday.toLocaleDateString('it-IT')}`
        } else if (chartScale === 'month') {
          const year = d.getFullYear()
          const month = d.getMonth()
          const first = new Date(year, month, 1)
          sortTime = first.getTime()
          key = `month_${year}_${month}`
          label = `${String(month + 1).padStart(2, '0')}/${year}`
        } else {
          sortTime = d.getTime()
          key = `day_${sortTime}`
          label = d.toLocaleDateString('it-IT')
        }
      } else {
        key = String(r.day)
        label = formatDate(r.day)
        sortTime = Date.now()
      }

      if (!map.has(key)) {
        map.set(key, { label, totalTokens: 0, sortTime })
      }
      const agg = map.get(key)
      agg.totalTokens += r.totalTokens || 0
    }
    return Array.from(map.values())
      .sort((a, b) => a.sortTime - b.sortTime)
      .map(({ sortTime, ...rest }) => rest)
  }, [rows, chartScale])

  const chartUser = useMemo(() => {
    const map = new Map()
    const seriesSet = new Set()

    for (const r of rows) {
      const d = new Date(r.day)
      let key
      let label
      let sortTime

      if (!Number.isNaN(d.getTime())) {
        if (chartScale === 'week') {
          const day = d.getDay()
          const diff = day === 0 ? -6 : 1 - day
          const monday = new Date(d)
          monday.setDate(d.getDate() + diff)
          sortTime = monday.getTime()
          key = `week_${sortTime}`
          label = `Sett. ${monday.toLocaleDateString('it-IT')}`
        } else if (chartScale === 'month') {
          const year = d.getFullYear()
          const month = d.getMonth()
          const first = new Date(year, month, 1)
          sortTime = first.getTime()
          key = `month_${year}_${month}`
          label = `${String(month + 1).padStart(2, '0')}/${year}`
        } else {
          sortTime = d.getTime()
          key = `day_${sortTime}`
          label = d.toLocaleDateString('it-IT')
        }
      } else {
        key = String(r.day)
        label = formatDate(r.day)
        sortTime = Date.now()
      }

      const seriesKey = r.userName || r.email || `Utente ${r.userId || 'n/d'}`
      if (!map.has(key)) {
        map.set(key, { label, sortTime })
      }
      const row = map.get(key)
      row[seriesKey] = (row[seriesKey] || 0) + (r.totalTokens || 0)
      seriesSet.add(seriesKey)
    }

    const data = Array.from(map.values())
      .sort((a, b) => a.sortTime - b.sortTime)
      .map(({ sortTime, ...rest }) => rest)

    const seriesKeys = Array.from(seriesSet)
    return { data, seriesKeys }
  }, [rows, chartScale])

  const chartModel = useMemo(() => {
    const map = new Map()
    const seriesSet = new Set()

    for (const r of rows) {
      const d = new Date(r.day)
      let key
      let label
      let sortTime

      if (!Number.isNaN(d.getTime())) {
        if (chartScale === 'week') {
          const day = d.getDay()
          const diff = day === 0 ? -6 : 1 - day
          const monday = new Date(d)
          monday.setDate(d.getDate() + diff)
          sortTime = monday.getTime()
          key = `week_${sortTime}`
          label = `Sett. ${monday.toLocaleDateString('it-IT')}`
        } else if (chartScale === 'month') {
          const year = d.getFullYear()
          const month = d.getMonth()
          const first = new Date(year, month, 1)
          sortTime = first.getTime()
          key = `month_${year}_${month}`
          label = `${String(month + 1).padStart(2, '0')}/${year}`
        } else {
          sortTime = d.getTime()
          key = `day_${sortTime}`
          label = d.toLocaleDateString('it-IT')
        }
      } else {
        key = String(r.day)
        label = formatDate(r.day)
        sortTime = Date.now()
      }

      const seriesKey = r.model || 'Modello n/d'
      if (!map.has(key)) {
        map.set(key, { label, sortTime })
      }
      const row = map.get(key)
      row[seriesKey] = (row[seriesKey] || 0) + (r.totalTokens || 0)
      seriesSet.add(seriesKey)
    }

    const data = Array.from(map.values())
      .sort((a, b) => a.sortTime - b.sortTime)
      .map(({ sortTime, ...rest }) => rest)

    const seriesKeys = Array.from(seriesSet)
    return { data, seriesKeys }
  }, [rows, chartScale])

  const handleExportCsv = async () => {
    if (!rows.length) return
    try {
      setExporting(true)
      let header = []
      let dataRows = []

      if (viewMode === 'byUser') {
        header = ['Utente', 'Email', 'PromptTokens', 'CompletionTokens', 'TotalTokens']
        dataRows = userRows.map((r) => [
          r.userName || '',
          r.email || '',
          r.promptTokens || 0,
          r.completionTokens || 0,
          r.totalTokens || 0
        ])
      } else if (viewMode === 'byModel') {
        header = ['Model', 'PromptTokens', 'CompletionTokens', 'TotalTokens']
        dataRows = modelRows.map((r) => [
          r.model || '',
          r.promptTokens || 0,
          r.completionTokens || 0,
          r.totalTokens || 0
        ])
      } else {
        header = ['Date', 'User', 'Email', 'Model', 'PromptTokens', 'CompletionTokens', 'TotalTokens']
        dataRows = detailRows.map((r) => [
          formatDate(r.day),
          r.userName || '',
          r.email || '',
          r.model || '',
          r.promptTokens || 0,
          r.completionTokens || 0,
          r.totalTokens || 0
        ])
      }

      const csvLines = [
        header.join(';'),
        ...dataRows.map((cols) => cols.join(';'))
      ]
      const csvContent = csvLines.join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const suffix =
        from && to
          ? `_${from.replace(/-/g, '')}_${to.replace(/-/g, '')}`
          : ''
      const viewLabel =
        viewMode === 'byUser' ? 'by_user' : viewMode === 'byModel' ? 'by_model' : 'detail'
      link.setAttribute('download', `ai_usage_${viewLabel}${suffix}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e) {
      toast.error('Errore durante l’esportazione CSV dei consumi AI')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">AI – Consumi token</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Report giornaliero per utente e modello, basato sulle richieste ai modelli AI.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={viewMode === 'detail' ? 'default' : 'outline'}
            className="text-xs"
            onClick={() => setViewMode('detail')}
          >
            Dettaglio
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'byUser' ? 'default' : 'outline'}
            className="text-xs"
            onClick={() => setViewMode('byUser')}
          >
            Per utente
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'byModel' ? 'default' : 'outline'}
            className="text-xs"
            onClick={() => setViewMode('byModel')}
          >
            Per modello
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-xs"
            onClick={handleExportCsv}
            disabled={exporting || !rows.length}
          >
            {exporting ? 'Esportazione…' : 'Esporta CSV'}
          </Button>
        </div>
      </div>

      <Card className="p-4 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Dal giorno</label>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Al giorno</label>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={loadUsage} disabled={loading || !from || !to}>
              {loading ? 'Caricamento...' : 'Aggiorna'}
            </Button>
            <Badge variant="outline" className="text-[10px]">
              Totale periodo: {totalPeriodTokens.toLocaleString('it-IT')} token
            </Badge>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                className="text-[10px]"
                onClick={() => applyQuickRange('day')}
              >
                Ultimi 7 giorni
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-[10px]"
                onClick={() => applyQuickRange('week')}
              >
                Ultime 12 sett.
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-[10px]"
                onClick={() => applyQuickRange('month')}
              >
                Ultimi 6 mesi
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-4 overflow-x-auto">
        {loading && <div className="text-sm text-muted-foreground">Caricamento consumi AI...</div>}
        {!loading && rows.length === 0 && (
          <div className="text-sm text-muted-foreground">
            Nessun consumo registrato nel periodo selezionato.
          </div>
        )}
        {!loading && rows.length > 0 && viewMode === 'detail' && (
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Data</th>
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Utente</th>
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Email</th>
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Modello</th>
                <th className="text-right py-2 pr-4 font-medium text-muted-foreground">Prompt</th>
                <th className="text-right py-2 pr-4 font-medium text-muted-foreground">Completion</th>
                <th className="text-right py-2 pr-0 font-medium text-muted-foreground">Totale</th>
              </tr>
            </thead>
            <tbody>
              {detailRows.map((r, idx) => (
                <tr key={`${r.day}-${r.userId || 'null'}-${r.model || 'model'}-${idx}`} className="border-b last:border-0">
                  <td className="py-1.5 pr-4 whitespace-nowrap">{formatDate(r.day)}</td>
                  <td className="py-1.5 pr-4 whitespace-nowrap">{r.userName || '-'}</td>
                  <td className="py-1.5 pr-4 whitespace-nowrap">{r.email || '-'}</td>
                  <td className="py-1.5 pr-4 whitespace-nowrap">{r.model || '-'}</td>
                  <td className="py-1.5 pr-4 text-right">
                    {formatPromptValue(r.promptTokens, r.completionTokens, r.totalTokens)}
                  </td>
                  <td className="py-1.5 pr-4 text-right">
                    {formatCompletionValue(r.promptTokens, r.completionTokens, r.totalTokens)}
                  </td>
                  <td className="py-1.5 pr-0 text-right font-semibold">
                    {(r.totalTokens || 0).toLocaleString('it-IT')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && rows.length > 0 && viewMode === 'byUser' && (
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Utente</th>
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Email</th>
                <th className="text-right py-2 pr-4 font-medium text-muted-foreground">Prompt</th>
                <th className="text-right py-2 pr-4 font-medium text-muted-foreground">Completion</th>
                <th className="text-right py-2 pr-0 font-medium text-muted-foreground">Totale</th>
              </tr>
            </thead>
            <tbody>
              {userRows.map((r, idx) => (
                <tr key={`${r.userId || 'null'}-${idx}`} className="border-b last:border-0">
                  <td className="py-1.5 pr-4 whitespace-nowrap">{r.userName || '-'}</td>
                  <td className="py-1.5 pr-4 whitespace-nowrap">{r.email || '-'}</td>
                  <td className="py-1.5 pr-4 text-right">
                    {formatPromptValue(r.promptTokens, r.completionTokens, r.totalTokens)}
                  </td>
                  <td className="py-1.5 pr-4 text-right">
                    {formatCompletionValue(r.promptTokens, r.completionTokens, r.totalTokens)}
                  </td>
                  <td className="py-1.5 pr-0 text-right font-semibold">
                    {(r.totalTokens || 0).toLocaleString('it-IT')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && rows.length > 0 && viewMode === 'byModel' && (
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Modello</th>
                <th className="text-right py-2 pr-4 font-medium text-muted-foreground">Prompt</th>
                <th className="text-right py-2 pr-4 font-medium text-muted-foreground">Completion</th>
                <th className="text-right py-2 pr-0 font-medium text-muted-foreground">Totale</th>
              </tr>
            </thead>
            <tbody>
              {modelRows.map((r, idx) => (
                <tr key={`${r.model || 'model'}-${idx}`} className="border-b last:border-0">
                  <td className="py-1.5 pr-4 whitespace-nowrap">{r.model || '-'}</td>
                  <td className="py-1.5 pr-4 text-right">
                    {formatPromptValue(r.promptTokens, r.completionTokens, r.totalTokens)}
                  </td>
                  <td className="py-1.5 pr-4 text-right">
                    {formatCompletionValue(r.promptTokens, r.completionTokens, r.totalTokens)}
                  </td>
                  <td className="py-1.5 pr-0 text-right font-semibold">
                    {(r.totalTokens || 0).toLocaleString('it-IT')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-sm font-semibold">
              {viewMode === 'byUser'
                ? 'Andamento token per utente'
                : viewMode === 'byModel'
                ? 'Andamento token per modello'
                : 'Andamento totale token'}
            </h2>
            <p className="text-xs text-muted-foreground">
              {viewMode === 'byUser'
                ? 'Token giornalieri per utente nel periodo selezionato.'
                : viewMode === 'byModel'
                ? 'Token giornalieri per modello nel periodo selezionato.'
                : 'Somma giornaliera dei token consumati nel periodo selezionato.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={chartScale === 'day' ? 'default' : 'outline'}
              className="text-[10px]"
              onClick={() => setChartScale('day')}
            >
              Giorno
            </Button>
            <Button
              size="sm"
              variant={chartScale === 'week' ? 'default' : 'outline'}
              className="text-[10px]"
              onClick={() => setChartScale('week')}
            >
              Settimana
            </Button>
            <Button
              size="sm"
              variant={chartScale === 'month' ? 'default' : 'outline'}
              className="text-[10px]"
              onClick={() => setChartScale('month')}
            >
              Mese
            </Button>
          </div>
        </div>
        <div className="h-64">
          {viewMode === 'detail' && (
            <>
              {chartDetailData.length === 0 ? (
                <div className="text-sm text-muted-foreground flex items-center h-full">
                  Nessun dato disponibile per il grafico.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartDetailData}>
                    <defs>
                      <linearGradient id="aiTokensGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="label" stroke="#888888" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#888888" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      formatter={(value) => [Number(value).toLocaleString('it-IT'), 'Token']}
                      labelFormatter={(label) => `Data: ${label}`}
                    />
                    <Area
                      type="monotone"
                      dataKey="totalTokens"
                      stroke="#6366f1"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#aiTokensGradient)"
                      name="Token totali"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </>
          )}

          {viewMode === 'byUser' && (
            <>
              {chartUser.data.length === 0 ? (
                <div className="text-sm text-muted-foreground flex items-center h-full">
                  Nessun dato disponibile per il grafico.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartUser.data}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="label" stroke="#888888" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#888888" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      formatter={(value) => [Number(value).toLocaleString('it-IT'), 'Token']}
                      labelFormatter={(label) => `Data: ${label}`}
                    />
                    <Legend />
                    {chartUser.seriesKeys.map((key, index) => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        name={key}
                        stroke={['#6366f1', '#22c55e', '#f97316', '#0ea5e9', '#e11d48'][index % 5]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </>
          )}

          {viewMode === 'byModel' && (
            <>
              {chartModel.data.length === 0 ? (
                <div className="text-sm text-muted-foreground flex items-center h-full">
                  Nessun dato disponibile per il grafico.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartModel.data}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="label" stroke="#888888" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#888888" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                      formatter={(value) => [Number(value).toLocaleString('it-IT'), 'Token']}
                      labelFormatter={(label) => `Data: ${label}`}
                    />
                    <Legend />
                    {chartModel.seriesKeys.map((key, index) => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        name={key}
                        stroke={['#6366f1', '#22c55e', '#f97316', '#0ea5e9', '#e11d48'][index % 5]}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  )
}

export default AiUsageAdmin
